import React, { useState, useEffect, forwardRef, useRef, useImperativeHandle } from 'react';
import { Check, XCircle } from '@phosphor-icons/react';

import { FilterProps, FilterValue, Operator } from './types';
import { useMultiSelectHelpers } from './hooks';
import { CustomValueInput } from './components/CustomValueInput';
import { MultiSelectDropdown } from './components/MultiSelectDropdown';
import { SingleSelectDropdown } from './components/SingleSelectDropdown';

import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button-group';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

const Filter = forwardRef<{ focus: () => void }, FilterProps>(
  (
    {
      filter,
      sourceOptions,
      rules,
      onSourceChange,
      onRemoveFilter,
      onFilterChange,
      selectPlaceholder = 'Select a value',
      customValuePlaceholder = 'Enter custom value',
      customValueButtonLabel = 'Done',
      sourceFirstMessage = 'Please select a source first',
    }: FilterProps,
    ref,
  ) => {
    // State
    const [internalFilter, setInternalFilter] = useState<FilterValue>({
      id: filter.id,
      source: filter.source,
      operator: filter.operator || Operator.IS,
      value: filter.value,
    });
    const [inputValue, setInputValue] = useState('');
    const [openDropdownType, setOpenDropdownType] = useState<'source' | 'operator' | 'value' | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const sourceButtonRef = useRef<HTMLButtonElement>(null);
    const divRef = useRef<HTMLDivElement>(null);

    // Expose focus method
    useImperativeHandle(ref, () => ({
      focus: () => {
        sourceButtonRef.current?.focus();
      },
    }));

    // Get current rules for selected source
    const currentRules = internalFilter.source ? rules[internalFilter.source] : undefined;
    const operatorOptions = currentRules?.operators || [];
    const isMultiSelect = currentRules?.multiSelect || false;
    let inputType = currentRules?.inputType || 'dropdown';
    if (internalFilter.operator === Operator.CONTAINS || internalFilter.operator === Operator.DOES_NOT_CONTAIN) {
      inputType = 'custom';
    }
    const valueOptions = currentRules?.getValueOptions ? currentRules.getValueOptions() : [];

    // Multi-select helpers
    const { getSelectedValues, isValueSelected, getDisplayValue } = useMultiSelectHelpers(
      internalFilter,
      valueOptions,
      selectPlaceholder,
    );

    // Auto-focus input when dropdown opens and custom input is needed
    useEffect(() => {
      if (openDropdownType === 'value' && inputType === 'custom') {
        const timeoutId = setTimeout(() => inputRef.current?.focus(), 50);

        return () => clearTimeout(timeoutId);
      }

      return undefined;
    }, [openDropdownType, inputType]);

    // Sync with external state
    useEffect(() => {
      setInternalFilter({
        id: filter.id,
        source: filter.source,
        operator: filter.operator || Operator.IS,
        value: filter.value,
      });
    }, [filter]);

    // Helper function to check if filter is complete and trigger onChange
    const checkAndTriggerChange = (newFilter: FilterValue) => {
      const hasValue = Array.isArray(newFilter.value) ? newFilter.value.length > 0 : !!newFilter.value?.trim();

      const isComplete = newFilter.source && newFilter.operator && hasValue;

      if (isComplete) {
        onFilterChange(newFilter);
      }
    };

    // Handle field updates
    const updateFilterField = (field: keyof FilterValue, value: string) => {
      const newFilter: FilterValue = { ...internalFilter, [field]: value };

      // Special handling for source changes
      if (field === 'source') {
        const sourceRules = rules[value];
        newFilter.operator = sourceRules?.operators[0]?.id as Operator;
        newFilter.value = sourceRules?.multiSelect ? [] : '';
      }
      // If operator changes, also clear value if inputType is dropdown
      if (field === 'operator') {
        if (internalFilter.source) {
          const sourceRules = rules[internalFilter.source];
          if (sourceRules?.inputType === 'dropdown') {
            newFilter.value = sourceRules?.multiSelect ? [] : '';
          }
        }
      }

      setInternalFilter(newFilter);
      checkAndTriggerChange(newFilter);

      if (!isMultiSelect || field !== 'value') {
        setOpenDropdownType(null);
      }
    };

    // Handle multi-select value changes
    const handleValueToggle = (valueId: string) => {
      const selectedValues = getSelectedValues();
      let newValues: string[];

      if (isMultiSelect) {
        if (selectedValues.includes(valueId)) {
          newValues = selectedValues.filter((id) => id !== valueId);
        } else {
          newValues = [...selectedValues, valueId];
        }
      } else {
        newValues = [valueId];
      }

      const newFilter = {
        ...internalFilter,
        value: isMultiSelect ? newValues : newValues[0] || '',
      };

      setInternalFilter(newFilter);
      checkAndTriggerChange(newFilter);

      // Close dropdown for single select, keep open for multi-select
      if (!isMultiSelect) {
        setOpenDropdownType(null);
      }
    };

    // Handle multi-select confirmation
    const handleMultiSelectConfirm = (selectedValues: string[]) => {
      const newFilter = {
        ...internalFilter,
        value: selectedValues,
      };

      setInternalFilter(newFilter);

      // Trigger onChange if filter is complete
      const isComplete = newFilter.source && newFilter.operator && selectedValues.length > 0;
      if (isComplete) onFilterChange(newFilter);

      setOpenDropdownType(null);
    };

    // Handle custom value submission
    const handleCustomValueSubmit = () => {
      if (inputValue.trim()) {
        const newFilter = { ...internalFilter, value: inputValue.trim() };
        setInternalFilter(newFilter);
        setInputValue('');
        setOpenDropdownType(null);
        if (newFilter.source && newFilter.operator && newFilter.value) {
          onFilterChange(newFilter);
        }
      }
    };

    // Tab trap handlers
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLButtonElement>, isInput: boolean) => {
      if (e.key === 'Escape') {
        setOpenDropdownType(null);

        return;
      }
      if (e.key === 'Tab') {
        if (isInput && !e.shiftKey) {
          e.preventDefault();
          const button = document.querySelector('[data-custom-submit-button]') as HTMLButtonElement;
          button?.focus();
        } else if (!isInput) {
          e.preventDefault();
          inputRef.current?.focus();
        }
      }
    };

    return (
      <ButtonGroup
        ref={divRef}
        orientation="horizontal"
        className="rounded-full border border-border divide-x-2 divide-dark-500"
      >
        {/* Source Dropdown */}
        <DropdownMenu
          open={openDropdownType === 'source'}
          onOpenChange={(open) => setOpenDropdownType(open ? 'source' : null)}
          modal={false}
        >
          <DropdownMenuTrigger asChild>
            <Button ref={sourceButtonRef} variant="ghost" size="xs" className="rounded-l-full font-bold">
              {sourceOptions.find((opt) => opt.id === internalFilter.source)?.label ||
                sourceOptions[0]?.label ||
                selectPlaceholder}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-48">
            {sourceOptions.map((option) => (
              <DropdownMenuItem
                key={option.id}
                className="flex items-center"
                onSelect={() => {
                  updateFilterField('source', option.id);
                  onSourceChange?.(option.id);
                }}
              >
                {option.id === internalFilter.source && <Check size={14} className="mr-2" />}
                {option.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Operator Dropdown */}
        <DropdownMenu
          open={openDropdownType === 'operator'}
          onOpenChange={(open) => setOpenDropdownType(open ? 'operator' : null)}
          modal={false}
        >
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="xs">
              {operatorOptions.find((opt) => opt.id === internalFilter.operator)?.label || selectPlaceholder}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-48">
            {operatorOptions.map((option) => (
              <DropdownMenuItem
                key={option.id}
                className="flex items-center"
                onSelect={() => updateFilterField('operator', option.id)}
              >
                {option.id === internalFilter.operator && <Check size={14} className="mr-2" />}
                {option.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Value Popover */}
        <Popover
          open={openDropdownType === 'value'}
          onOpenChange={(open) => setOpenDropdownType(open ? 'value' : null)}
        >
          <PopoverTrigger asChild>
            <Button variant="ghost" size="xs" className="font-bold">
              {getDisplayValue()}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-64 max-h-[min(320px,50vh)] overflow-y-auto p-0">
            {inputType === 'custom' && (
              <div className="p-3">
                <CustomValueInput
                  inputValue={inputValue}
                  setInputValue={setInputValue}
                  customValuePlaceholder={customValuePlaceholder}
                  customValueButtonLabel={customValueButtonLabel}
                  onSubmit={handleCustomValueSubmit}
                  handleKeyDown={handleKeyDown}
                />
              </div>
            )}
            {inputType === 'dropdown' && (
              <>
                {internalFilter.source ? (
                  <>
                    {isMultiSelect ? (
                      <MultiSelectDropdown
                        internalFilter={internalFilter}
                        valueOptions={valueOptions}
                        onConfirm={handleMultiSelectConfirm}
                        onClose={() => setOpenDropdownType(null)}
                      />
                    ) : (
                      <SingleSelectDropdown
                        valueOptions={valueOptions}
                        isValueSelected={isValueSelected}
                        onValueSelect={handleValueToggle}
                      />
                    )}
                  </>
                ) : (
                  <div className="p-3 text-sm text-gray-500">{sourceFirstMessage}</div>
                )}
              </>
            )}
          </PopoverContent>
        </Popover>

        {/* Remove button */}
        <Button
          type="button"
          variant="ghost"
          size="xs"
          onClick={() => {
            setInternalFilter({ id: filter.id });
            setOpenDropdownType(null);
            onRemoveFilter(filter.id);
          }}
          className="p-2 hover:bg-black/10 rounded-none rounded-r-full"
          aria-label="Remove filter"
        >
          <XCircle size={16} weight="fill" />
        </Button>
      </ButtonGroup>
    );
  },
);

Filter.displayName = 'Filter';

export { Filter };
